/**
 * Voice-activated barge-in detector — WEB (Phase B).
 *
 * Runs a lightweight energy VAD (getUserMedia + AudioContext) while the coach is
 * speaking and fires once the user starts speaking, so the coach yields
 * hands-free. Uses the browser's echo-cancellation constraint to reduce the
 * coach's own audio self-triggering it — but echo behaviour is exactly what a
 * hardware/browser spike must measure (design §9.3), so treat this as the
 * experimental path; tap-to-interrupt remains the guaranteed fallback.
 *
 * AudioWorklet is the production upgrade; an AnalyserNode RMS is fine for a spike.
 */
import { VOICE_ENABLED } from "./flag";

/** Sustained energy (ms) that counts as a real barge-in, not a transient. */
const ONSET_MS = 300;
/** RMS (0..1) above which a frame is "voiced". Tune per environment. */
const VOICED_RMS = 0.05;

export const BARGE_VAD_SUPPORTED =
  VOICE_ENABLED &&
  typeof navigator !== "undefined" &&
  Boolean(navigator.mediaDevices?.getUserMedia);

/**
 * Listen for a user speech onset. Calls `onSpeechOnset` once (then auto-stops).
 * Returns a stop function. No-op (returns a noop) when unsupported.
 */
export async function startBargeVad(
  onSpeechOnset: () => void,
): Promise<() => void> {
  const noop = () => {};
  if (!BARGE_VAD_SUPPORTED) return noop;

  let stream: MediaStream | null = null;
  let ctx: AudioContext | null = null;
  let raf = 0;
  let voicedSince: number | null = null;
  let stopped = false;

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    void ctx?.close().catch(() => {});
  };

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const Ctx =
      (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return noop;
    ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);

    const loop = () => {
      if (stopped) return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (rms >= VOICED_RMS) {
        if (voicedSince === null) voicedSince = now;
        else if (now - voicedSince >= ONSET_MS) {
          cleanup();
          onSpeechOnset();
          return;
        }
      } else {
        voicedSince = null;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return cleanup;
  } catch {
    cleanup();
    return noop;
  }
}
